import { RouteProp } from '@react-navigation/native';
import React from 'react';
import { ProfilesTabParamList, useGoBack, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import useNotes from '../../../../../hooks/api/useNotes';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import NoteDetails from '../../../../shared/Notes/NoteDetails';
import NoteLinkPill from '../../../../shared/Notes/NoteDetails/NoteLinkPill';
import { getIntl } from '../../../../../utils/intl';
import { APITypes } from '../../../../../primary-record-types';
import Text from '../../../../v2BaseComponents/Text';
import Stack, { contentStackSpacing } from '../../../../v2BaseComponents/Stack';

const intl = getIntl('client-record-notes-details', {
    transferNote: 'Move Note',
    delete: 'Delete Note',
    retainedMessage:
        "Retained copy. The connection to this client's profile has ended, or the source note was removed; this copy and its comments are yours.",
    retainedEditedMessage:
        "Retained copy. It was edited here, so it no longer tracks the note in the client's profile; this copy and its comments are yours.",
});

export type ClientRecordNoteDetailsScreenOwnProps = {};

export type ClientRecordNoteDetailsScreenProps = ClientRecordNoteDetailsScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'client-record-note'>;
};

type SourceNoteLocation = {
    noteId: string;
    profileId: string;
    noteEntityType: NonNullable<APITypes.notesService.NoteMirrorMetadata['sourceNoteEntityType']>;
};

/**
 * Where the copy's source note can be opened, or null when the reader may no longer view it. A retained copy edited
 * by hand while the organization is connected still has a source to open.
 */
const getSourceNoteLocation = (mirror: APITypes.notesService.NoteMirrorMetadata): SourceNoteLocation | null =>
    mirror.canOpenSource && mirror.sourceMedicalProfileId && mirror.sourceNoteEntityType
        ? {
              noteId: mirror.sourceNoteId,
              profileId: mirror.sourceMedicalProfileId,
              noteEntityType: mirror.sourceNoteEntityType,
          }
        : null;

const ClientRecordNoteDetailsScreen: React.FC<ClientRecordNoteDetailsScreenProps> = ({ route }) => {
    const goBack = useGoBack();
    const navigator = useProfilesTabNavigation();
    const { isLoading, note, deleteNote, isDeletingNote } = useNotes({
        noteId: route.params.noteId,
        onNoteLoadFailure: () => {
            goBack();
        },
    });

    const mirror = note?.mirror ?? null;
    const source = mirror ? getSourceNoteLocation(mirror) : null;
    // A live copy is edited on its profile note, so Edit opens that note's editor; a live copy whose source the
    // reader cannot open has nowhere to be edited. A copy that is no longer live is edited here like any other note.
    const isEditedOnSource = !!mirror?.isLive;
    const canEdit = !isDeletingNote && !note?.isSystemGenerated && (!isEditedOnSource || !!source);

    return (
        <AnimateLoadedContent isLoading={isDeletingNote || isLoading || !note}>
            {() =>
                note ? (
                    <NoteDetails
                        note={note}
                        canEdit={canEdit}
                        banner={
                            // A retained copy explains itself; a live one is explained by its pill and its Edit.
                            mirror && !mirror.isLive ? (
                                <Stack
                                    vertical
                                    fullWidth
                                    space={contentStackSpacing}
                                    testID="note-mirror-retained-banner"
                                >
                                    <Text variant="body-2" color="Gray-Text">
                                        {intl(source ? 'retainedEditedMessage' : 'retainedMessage')}
                                    </Text>
                                </Stack>
                            ) : null
                        }
                        linkPill={
                            mirror ? (
                                <NoteLinkPill
                                    state={mirror.isLive ? 'linked' : 'retained'}
                                    onPress={source ? () => navigator.navigate('note', source) : undefined}
                                    testID={mirror.isLive ? 'note-link-pill-linked' : 'note-link-pill-retained'}
                                />
                            ) : null
                        }
                        onEdit={() => {
                            if (isEditedOnSource && source) {
                                navigator.navigate('edit-note', {
                                    noteId: source.noteId,
                                    noteEntityType: source.noteEntityType,
                                    profileId: source.profileId,
                                });
                                return;
                            }
                            navigator.navigate('edit-client-record-note', {
                                noteId: note.noteId,
                                clientRecordId: route.params.clientRecordId,
                            });
                        }}
                        editActions={[
                            {
                                icon: 'Trash2',
                                title: intl('delete'),
                                onPress: () => {
                                    deleteNote(route.params.noteId);
                                    goBack();
                                },
                                isDisabled: isDeletingNote,
                                testID: 'delete-note-button',
                            },
                        ]}
                    />
                ) : null
            }
        </AnimateLoadedContent>
    );
};

export default React.memo(ClientRecordNoteDetailsScreen);
